/**
 * AI cost reconciliation (TMMPDM-0088, tmm-api 0152; 0153 section G item 31): a read-only report,
 * one row per UTC day and currency, newest day first. `from` / `to` (UTC dates) live in the URL
 * (TMM-B6); the default is the last 30 days ending today, UTC. A range the server would refuse
 * (start after end, or more than 366 days) is said here and never sent.
 *
 * Every figure is shown as sent, in its own row's currency. Nothing is summed across rows or
 * currencies, and there is no totals row. Each must-be-zero (†) cell is judged from its own value;
 * the Breached chip comes from the row's `mustBeZeroBreached` alone. TMMPDM-0104 (TMMAPI-0171):
 * Truncated and Refused follow Sent, unsettled as plain counts with a header help line.
 */
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/api/tmm/admin';
import { ApiError } from '@/api/errors';
import type { AiCostReconciliationView } from '@/api/tmm/types';
import { EmptyState, ErrorState, PageHeader } from '@/components/page';
import { Amount, HelpPopover } from '@/components/ui';
import { useCopy } from '@/hooks/useCopy';
import { useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { NOT_SENT, formatInstant } from '@/format';
import { StatusChip } from '@/status';

const DAY_MS = 86_400_000;
/** The server's own limit (at most 366 days, inclusive). */
const MAX_DAYS = 366;

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

function utcDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function parseUtcDate(raw: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return undefined;
  const ms = Date.parse(`${raw}T00:00:00Z`);
  return Number.isNaN(ms) || utcDate(ms) !== raw ? undefined : raw;
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

type RangeView = { from: string; to: string };

/** The must-be-zero counts (†), in table order. */
const MUST_BE_ZERO = [
  'providerRejectedAuth',
  'releasedAfterSend',
  'costOverHold',
  'sentUnsettled',
] as const;
type MustBeZero = (typeof MUST_BE_ZERO)[number];

type CountKey =
  | 'requestsSent'
  | 'settledActual'
  | 'settledAtHold'
  | 'holdExpiredCharged'
  | 'providerRejected'
  | 'truncated'
  | 'refused'
  | MustBeZero;

const COUNT_COLUMNS: { key: CountKey; labelKey: string; fallback: string }[] = [
  { key: 'requestsSent', labelKey: 'aiReconciliation.col.sent', fallback: 'Sent' },
  {
    key: 'settledActual',
    labelKey: 'aiReconciliation.col.settledActual',
    fallback: 'Settled at usage',
  },
  {
    key: 'settledAtHold',
    labelKey: 'aiReconciliation.col.settledAtHold',
    fallback: 'Settled at the hold',
  },
  {
    key: 'holdExpiredCharged',
    labelKey: 'aiReconciliation.col.holdExpiredCharged',
    fallback: 'Hold expired, charged',
  },
  {
    key: 'providerRejected',
    labelKey: 'aiReconciliation.col.providerRejected',
    fallback: 'Provider rejected',
  },
  {
    key: 'providerRejectedAuth',
    labelKey: 'aiReconciliation.col.providerRejectedAuth',
    fallback: 'Rejected: auth',
  },
  {
    key: 'releasedAfterSend',
    labelKey: 'aiReconciliation.col.releasedAfterSend',
    fallback: 'Released after send',
  },
  {
    key: 'costOverHold',
    labelKey: 'aiReconciliation.col.costOverHold',
    fallback: 'Cost over the hold',
  },
  {
    key: 'sentUnsettled',
    labelKey: 'aiReconciliation.col.sentUnsettled',
    fallback: 'Sent, unsettled',
  },
  { key: 'truncated', labelKey: 'aiReconciliation.col.truncated', fallback: 'Truncated' },
  { key: 'refused', labelKey: 'aiReconciliation.col.refused', fallback: 'Refused' },
];

type MicroKey = 'customerProviderCostMicro' | 'customerUsageMicro' | 'platformCostMicro';

const MONEY_COLUMNS: { key: MicroKey; labelKey: string; fallback: string }[] = [
  {
    key: 'customerProviderCostMicro',
    labelKey: 'aiReconciliation.col.providerCost',
    fallback: 'Provider cost',
  },
  {
    key: 'customerUsageMicro',
    labelKey: 'aiReconciliation.col.customerUsage',
    fallback: 'Customer usage',
  },
  {
    key: 'platformCostMicro',
    labelKey: 'aiReconciliation.col.platformCost',
    fallback: 'Platform-paid cost',
  },
];

const isMustBeZero = (key: CountKey): key is MustBeZero =>
  (MUST_BE_ZERO as readonly string[]).includes(key);

/** Newest day first, then currency A–Z (presentation order only). */
function ordered(rows: AiCostReconciliationView[]): AiCostReconciliationView[] {
  return [...rows].sort((a, b) =>
    a.day === b.day
      ? (a.currency ?? '').localeCompare(b.currency ?? '')
      : b.day.localeCompare(a.day),
  );
}

export function AiCostReconciliationPage() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  // The default range is fixed when the page opens: the last 30 days ending today, UTC.
  const [schema] = useState<UrlSchema<RangeView>>(() => {
    const today = Date.now();
    return {
      from: { default: utcDate(today - 29 * DAY_MS), parse: parseUtcDate },
      to: { default: utcDate(today), parse: parseUtcDate },
    };
  });
  const [view, setView] = useUrlState(schema);
  const [reloadToken, setReloadToken] = useState(0);
  const [result, setResult] = useState<{
    key: string;
    rows: AiCostReconciliationView[] | null;
    failure: unknown;
  } | null>(null);

  const span = daysBetween(view.from, view.to);
  const rangeError =
    span < 0
      ? t('aiReconciliation.range.order', 'The start must be on or before the end.')
      : span + 1 > MAX_DAYS
        ? t('aiReconciliation.range.tooLong', 'Choose at most 366 days.')
        : null;
  const requestKey = `${view.from}:${view.to}:${reloadToken}`;

  useEffect(() => {
    if (rangeError !== null) return;
    let cancelled = false;
    adminApi
      .aiCostReconciliation({ from: view.from, to: view.to })
      .then((body) => {
        if (!cancelled) setResult({ key: requestKey, rows: body.days, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setResult({ key: requestKey, rows: null, failure: failure ?? 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [view.from, view.to, requestKey, rangeError]);

  const current = result?.key === requestKey ? result : null;
  const rows = useMemo(() => (current?.rows ? ordered(current.rows) : null), [current]);

  const mustBeZeroText = t('aiReconciliation.mustBeZero', 'must be 0');
  // Header help lines (TMMPDM-0104): plain counts, not must-be-zero.
  const countHelp: Partial<Record<CountKey, string>> = {
    truncated: t(
      'aiReconciliation.help.truncated',
      'Answers cut off at the token limit. Charged at their usage.',
    ),
    refused: t('aiReconciliation.help.refused', 'Requests the model refused.'),
  };
  const header = (label: string, dagger: boolean, help?: string) => (
    <TableCell align="right" sx={{ fontWeight: 600 }}>
      {label}
      {help ? <HelpPopover label={label} short={help} /> : null}
      {dagger ? (
        <>
          {' '}
          <span aria-hidden="true">†</span>
          <Box component="span" sx={visuallyHidden}>
            {` (${mustBeZeroText})`}
          </Box>
        </>
      ) : null}
    </TableCell>
  );

  const countCell = (row: AiCostReconciliationView, key: CountKey): ReactNode => {
    const value = row[key];
    if (value === null || value === undefined)
      return <TableCell align="right">{NOT_SENT}</TableCell>;
    const flagged = isMustBeZero(key) && value !== 0;
    return (
      <TableCell align="right" sx={flagged ? { color: 'warning.main' } : undefined}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            justifyContent: 'flex-end',
          }}
        >
          {flagged ? <WarningAmberIcon fontSize="small" aria-hidden="true" /> : null}
          <Amount count={value} locale={locale} />
          {flagged ? (
            <Box component="span" sx={visuallyHidden}>
              {` ${t('aiReconciliation.shouldBeZero', 'should be 0')}`}
            </Box>
          ) : null}
        </Box>
      </TableCell>
    );
  };

  const body = (() => {
    if (rangeError !== null) return <Typography role="alert">{rangeError}</Typography>;
    if (current?.failure) {
      if (current.failure instanceof ApiError && current.failure.status === 400) {
        return (
          <Typography role="alert">
            {t(
              'aiReconciliation.range.refused',
              "These dates can't be shown. Choose at most 366 days, with the start on or before the end.",
            )}
          </Typography>
        );
      }
      return (
        <ErrorState
          error={current.failure}
          message={t('aiReconciliation.loadError', 'Could not load the AI cost reconciliation.')}
          onRetry={() => setReloadToken((n) => n + 1)}
        />
      );
    }
    if (rows === null) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('common.loading', 'Loading')}
        </Typography>
      );
    }
    if (rows.length === 0) {
      return (
        <EmptyState
          surface
          title={t('aiReconciliation.empty', 'No reconciliation rows for these days.')}
        />
      );
    }
    return (
      <>
        <TableContainer
          component={Paper}
          variant="outlined"
          role="region"
          aria-label={t('aiReconciliation.regionAria', 'AI cost reconciliation table')}
          tabIndex={0}
        >
          <Table
            size="small"
            aria-label={t('aiReconciliation.tableAria', 'AI cost reconciliation')}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('aiReconciliation.col.day', 'Day')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('aiReconciliation.col.currency', 'Currency')}
                </TableCell>
                {COUNT_COLUMNS.map((column) => (
                  <Fragment key={column.key}>
                    {header(
                      t(column.labelKey, column.fallback),
                      isMustBeZero(column.key),
                      countHelp[column.key],
                    )}
                  </Fragment>
                ))}
                {MONEY_COLUMNS.map((column) => (
                  <Fragment key={column.key}>
                    {header(t(column.labelKey, column.fallback), false)}
                  </Fragment>
                ))}
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('aiReconciliation.col.reconciledAt', 'Reconciled at')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.day}:${row.currency ?? ''}`}>
                  <TableCell>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <span>{row.day}</span>
                      {row.mustBeZeroBreached === true ? (
                        <StatusChip
                          tone="warning"
                          label={t('aiReconciliation.breached', 'Breached')}
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>{row.currency ?? NOT_SENT}</TableCell>
                  {COUNT_COLUMNS.map((column) => (
                    <Fragment key={column.key}>{countCell(row, column.key)}</Fragment>
                  ))}
                  {MONEY_COLUMNS.map((column) => (
                    <TableCell key={column.key} align="right">
                      {row.currency ? (
                        <Amount micro={row[column.key]} currency={row.currency} locale={locale} />
                      ) : (
                        NOT_SENT
                      )}
                    </TableCell>
                  ))}
                  <TableCell>{formatInstant(row.reconciledAt, locale)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('aiReconciliation.legend', '† Must be 0.')}
        </Typography>
      </>
    );
  })();

  return (
    <Box>
      <PageHeader
        title={t('page.aiReconciliation.title', 'AI cost reconciliation')}
        subtitle={t(
          'page.aiReconciliation.subtitle',
          'One row per UTC day and currency. The highlighted counts must be zero.',
        )}
        controls={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="date"
              size="small"
              label={t('aiReconciliation.from', 'From (UTC)')}
              value={view.from}
              onChange={(event) => {
                const next = parseUtcDate(event.target.value);
                if (next) setView({ from: next });
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="date"
              size="small"
              label={t('aiReconciliation.to', 'To (UTC)')}
              value={view.to}
              onChange={(event) => {
                const next = parseUtcDate(event.target.value);
                if (next) setView({ to: next });
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
        }
      />
      {body}
    </Box>
  );
}
